// src/pages/finance/ErrorMpesaTransactionsPage.tsx
import {
  AlertCircle,
  Ban,
  Banknote,
  CheckCircle2,
  Edit2,
  Loader2,
  RefreshCw,
  Save,
  Search,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useToast } from "../../../components/ui/Toast";
import { API_CONFIG, fetchApp } from "../../../services/apiService";

interface MpesaTx {
  id: number;
  trans_id: string;
  trans_time: string;
  trans_amount: number;
  business_short_code: string;
  bill_ref_number?: string | null;
  msisdn: string;
  processed?: boolean;
  process_error?: string | null;
  received_at?: string | null;
  processed_at?: string | null;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatKES = (cents: number) =>
  `KES ${new Intl.NumberFormat("en-KE").format(
    Math.round((Number(cents) || 0) / 100),
  )}`;

const formatDate = (value?: string | null) => {
  if (!value) return "—";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeList = <T,>(res: any): T[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  return [];
};

const BillRefPill: React.FC<{ value?: string | null }> = ({ value }) => {
  const hasValue = Boolean(value?.trim());

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-black ${
        hasValue
          ? "border-slate-300 bg-slate-200 text-slate-700"
          : "border-amber-200 bg-amber-100 text-amber-800"
      }`}
    >
      {hasValue ? value : "Missing"}
    </span>
  );
};

const ErrorMessage: React.FC<{ message?: string | null }> = ({ message }) => (
  <div className="flex items-start gap-2">
    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />

    <span className="max-w-[280px] break-words text-[12px] font-bold text-rose-700">
      {message || "Unknown error"}
    </span>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "rose" | "amber" | "slate" | "emerald";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const TableSkeleton: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 8 }).map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            i === 5 ? "w-48" : i === 7 ? "h-8 w-20 rounded-2xl" : "w-24"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-32 rounded bg-slate-200" />
        <div className="h-3 w-24 rounded bg-slate-200" />
      </div>
      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-14 rounded-2xl bg-slate-200" />
      <div className="h-14 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const ErrorMpesaTransactionsPage: React.FC = () => {
  const { showToast } = useToast();

  const [txs, setTxs] = useState<MpesaTx[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [editingTransId, setEditingTransId] = useState<string | null>(null);
  const [newBillRef, setNewBillRef] = useState("");
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setError(null);

    if (txs.length === 0) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const res = await fetchApp(
        `${API_CONFIG.baseUrl}/finances/mpesa-transactions`,
      );

      setTxs(normalizeList<MpesaTx>(res));
    } catch (e) {
      console.error(e);
      setError("Failed to load M-Pesa transactions.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorTxs = useMemo(() => {
    return txs.filter((tx) => Boolean(tx.process_error?.trim()));
  }, [txs]);

  const filtered = useMemo(() => {
    if (!search.trim()) return errorTxs;

    const q = search.toLowerCase();

    return errorTxs.filter((tx) =>
      [
        tx.trans_id,
        tx.bill_ref_number || "",
        tx.msisdn,
        tx.business_short_code,
        tx.process_error || "",
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [errorTxs, search]);

  const stats = useMemo(() => {
    const totalErrors = errorTxs.length;

    const totalAmount = errorTxs.reduce(
      (sum, tx) => sum + Number(tx.trans_amount || 0),
      0,
    );

    const errorTypes = new Map<string, number>();

    errorTxs.forEach((tx) => {
      const message = tx.process_error || "Unknown error";
      errorTypes.set(message, (errorTypes.get(message) || 0) + 1);
    });

    const sortedErrors = Array.from(errorTypes.entries()).sort(
      (a, b) => b[1] - a[1],
    );

    return {
      totalErrors,
      totalAmount,
      mostCommonError: sortedErrors[0]?.[0] || "No errors",
      errorCount: sortedErrors[0]?.[1] || 0,
      missingBillRefs: errorTxs.filter((tx) => !tx.bill_ref_number?.trim())
        .length,
    };
  }, [errorTxs]);

  const handleEditClick = (transId: string, currentBillRef: string) => {
    setEditingTransId(transId);
    setNewBillRef(currentBillRef || "");
  };

  const handleCancelEdit = () => {
    setEditingTransId(null);
    setNewBillRef("");
  };

  const handleSaveClick = async () => {
    if (!editingTransId) return;

    if (!newBillRef.trim()) {
      showToast("Please provide a new BillRefNumber", "error");
      return;
    }

    setSaving(true);

    try {
      const response = await fetchApp<any>(
        `${API_CONFIG.baseUrl}/payments/c2b/update_bill_ref/${editingTransId}`,
        {
          method: "PUT",
          body: JSON.stringify({ new_bill_ref: newBillRef.trim() }),
        },
      );

      if (response && response.message) {
        showToast(response.message, "success");
        setEditingTransId(null);
        setNewBillRef("");
        await loadData();
      } else {
        throw new Error(response?.detail || "Update failed");
      }
    } catch (e: any) {
      console.error(e);
      showToast(e?.message || "Failed to update BillRefNumber", "error");
    } finally {
      setSaving(false);
    }
  };

  const clearSearch = () => setSearch("");

  if (error && !txs.length && !loading) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={loadData}
              className={`${btnBase} border border-rose-200 bg-rose-100 px-3 py-2 text-rose-700 hover:bg-rose-200`}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-rose-500 via-amber-500 to-emerald-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-rose-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-amber-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-rose-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                  Payment Exceptions
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Failed M-Pesa Transactions
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Review failed C2B callbacks, correct missing or wrong bill
                  references, and reprocess payments safely.
                </p>
              </div>

              <button
                type="button"
                onClick={loadData}
                disabled={loading || refreshing}
                className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
              >
                {loading || refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {loading || refreshing ? "Loading…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing failed transaction records…
          </div>
        )}

        {error && txs.length > 0 && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Total Failures"
            value={stats.totalErrors}
            sub="Requires attention"
            icon={<Ban className="h-5 w-5" />}
            tone="rose"
          />

          <StatCard
            label="Stuck Revenue"
            value={formatKES(stats.totalAmount)}
            sub="Unprocessed funds"
            icon={<Banknote className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Missing Bill Ref"
            value={stats.missingBillRefs}
            sub="Need account correction"
            icon={<XCircle className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Top Issue"
            value={
              <span title={stats.mostCommonError}>{stats.mostCommonError}</span>
            }
            sub={`${stats.errorCount} occurrence${
              stats.errorCount !== 1 ? "s" : ""
            }`}
            icon={<AlertCircle className="h-5 w-5" />}
            tone={stats.totalErrors > 0 ? "rose" : "emerald"}
          />
        </div>

        {/* Main content */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          {/* Toolbar */}
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-rose-100 text-rose-700">
                <AlertCircle className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  Transaction Log
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  {loading
                    ? "Loading failed transactions…"
                    : `${filtered.length} of ${errorTxs.length} failed transactions`}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search TransID, phone, bill ref, error…"
                  className={`${inputCls} pl-10`}
                />
              </div>

              {search && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">TransID</th>
                  <th className="px-5 py-3.5 text-right font-black">Amount</th>
                  <th className="px-5 py-3.5 font-black">MSISDN</th>
                  <th className="px-5 py-3.5 font-black">Bill Ref</th>
                  <th className="px-5 py-3.5 font-black">Paybill</th>
                  <th className="px-5 py-3.5 font-black">Error</th>
                  <th className="px-5 py-3.5 font-black">Received</th>
                  <th className="px-5 py-3.5 text-right font-black">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableSkeleton key={i} />
                  ))
                ) : filtered.length > 0 ? (
                  filtered.map((tx) => {
                    const isEditing = editingTransId === tx.trans_id;

                    return (
                      <tr
                        key={tx.id}
                        className="transition-colors hover:bg-rose-50/50"
                      >
                        <td className="px-5 py-4">
                          <p className="font-mono text-[12.5px] font-black text-slate-900">
                            {tx.trans_id}
                          </p>
                        </td>

                        <td className="px-5 py-4 text-right font-mono text-[12.5px] font-black text-slate-800">
                          {formatKES(tx.trans_amount)}
                        </td>

                        <td className="px-5 py-4 font-mono text-[12px] font-bold text-slate-600">
                          {tx.msisdn}
                        </td>

                        <td className="px-5 py-4">
                          {isEditing ? (
                            <input
                              type="text"
                              value={newBillRef}
                              onChange={(e) => setNewBillRef(e.target.value)}
                              className="h-9 w-36 rounded-2xl border border-slate-300 bg-slate-50 px-3 text-[12.5px] font-bold text-slate-800 outline-none transition-all focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
                              placeholder="New account"
                              autoFocus
                            />
                          ) : (
                            <BillRefPill value={tx.bill_ref_number} />
                          )}
                        </td>

                        <td className="px-5 py-4 font-mono text-[12px] font-bold text-slate-500">
                          {tx.business_short_code}
                        </td>

                        <td className="px-5 py-4">
                          <ErrorMessage message={tx.process_error} />
                        </td>

                        <td className="px-5 py-4 text-[12px] font-medium text-slate-500">
                          {formatDate(tx.received_at || tx.trans_time)}
                        </td>

                        <td className="px-5 py-4 text-right">
                          {isEditing ? (
                            <div className="inline-flex items-center gap-2">
                              <button
                                type="button"
                                onClick={handleSaveClick}
                                disabled={saving}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 transition-colors hover:bg-emerald-200 disabled:opacity-50"
                                title="Save changes"
                              >
                                {saving ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                              </button>

                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                disabled={saving}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-200 text-slate-600 transition-colors hover:bg-slate-300 disabled:opacity-50"
                                title="Cancel"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                handleEditClick(
                                  tx.trans_id,
                                  tx.bill_ref_number || "",
                                )
                              }
                              className={`${btnBase} border border-slate-300 bg-slate-50 px-3 py-2 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-emerald-100">
                        <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No errors found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        {search
                          ? "No failed transactions match your search criteria."
                          : "Great job. All transactions have been processed successfully."}
                      </p>

                      {search && (
                        <button
                          type="button"
                          onClick={clearSearch}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear search
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : filtered.length > 0 ? (
              filtered.map((tx) => {
                const isEditing = editingTransId === tx.trans_id;

                return (
                  <div key={tx.id} className="px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-mono text-[13px] font-black text-slate-900">
                          {tx.trans_id}
                        </p>

                        <p className="mt-0.5 font-mono text-[12px] font-bold text-slate-500">
                          {tx.msisdn}
                        </p>
                      </div>

                      <span className="rounded-full border border-rose-200 bg-rose-100 px-2.5 py-1 text-[10.5px] font-black text-rose-700">
                        Failed
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                          Amount
                        </p>
                        <p className="mt-1 font-mono text-[12.5px] font-black text-slate-900">
                          {formatKES(tx.trans_amount)}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          Paybill
                        </p>
                        <p className="mt-1 font-mono text-[12.5px] font-black text-slate-800">
                          {tx.business_short_code}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Bill Ref / Account
                      </p>

                      {isEditing ? (
                        <input
                          type="text"
                          value={newBillRef}
                          onChange={(e) => setNewBillRef(e.target.value)}
                          className={`${inputCls} mt-2`}
                          placeholder="New account number"
                          autoFocus
                        />
                      ) : (
                        <div className="mt-2">
                          <BillRefPill value={tx.bill_ref_number} />
                        </div>
                      )}
                    </div>

                    <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-rose-700">
                        Error Description
                      </p>

                      <p className="mt-1 break-words text-[12px] font-bold text-rose-700">
                        {tx.process_error || "Unknown error"}
                      </p>
                    </div>

                    <p className="mt-3 text-[11.5px] font-medium text-slate-500">
                      Received: {formatDate(tx.received_at || tx.trans_time)}
                    </p>

                    {isEditing ? (
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={handleSaveClick}
                          disabled={saving}
                          className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/20`}
                        >
                          {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                          Save
                        </button>

                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          disabled={saving}
                          className={`${btnBase} h-10 border border-slate-300 bg-slate-50 text-slate-600 shadow-sm hover:bg-slate-100`}
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          handleEditClick(tx.trans_id, tx.bill_ref_number || "")
                        }
                        className={`${btnBase} mt-4 h-10 w-full border border-slate-300 bg-slate-50 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                      >
                        <Edit2 className="h-4 w-4" />
                        Correct Bill Ref
                      </button>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-emerald-100">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No errors found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  {search
                    ? "No failed transactions match your search criteria."
                    : "Great job. All transactions have been processed successfully."}
                </p>

                {search && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filtered.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {errorTxs.length}
              </span>{" "}
              failed transactions
            </p>

            {search && (
              <button
                type="button"
                onClick={clearSearch}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear search
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorMpesaTransactionsPage;
